import { useEffect, useMemo, useRef, useState } from 'react'
import { Table } from '@radix-ui/themes'
import { Text } from '@radix-ui/themes'
import { IconDotsVertical } from '@tabler/icons-react'
import { QuestionDetail, QuestionSection } from '@/types/question'
import { CaseStudyQuestion } from '@/types/question'
import { cn, truncate } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import {
  Table as UiTable,
  TableHeader as UiTableHeader,
  TableBody as UiTableBody,
  TableRow as UiTableRow,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
} from '@/components/ui/table'
import DataTable from '@/components/ui/table/data-table'
import QuestionDrawer from '@/features/question-lib/components/QuestionDrawer'
import { TypeValue } from '@/features/question-lib/constant'
import TablePaginate from '../table-paginate'
import SectionHeader from './section-header'
import {
  filterCaseStudyQuestionsByQuestionIds,
} from '@/utils/practiceTestSectionIds'

type SubQ = number | 'N/A'

type QuestionRow = {
  questionId: number
  moodleQuestionId: string
  question: string
  subQuestion: SubQ
  marks: number
  chapterRelated: string
  time: number
  avg_ai_marks?: number
  avg_tutor_marks?: number
}

function getRowFromQuestion(item: QuestionDetail): QuestionRow {
  const subQuestions = item?.sub_questions || []
  const marks = subQuestions.reduce((sum, sq) => sum + (sq?.marks || 0), 0)
  const time = subQuestions.reduce((sum, sq) => sum + (sq?.time_limit || 0), 0)
  const subQuestion: SubQ =
    subQuestions.length === 0 ? 'N/A' : subQuestions.length
  let chapterList: string[] | undefined = Array.isArray(item.chapters)
    ? item.chapters
    : undefined
  if (
    (!chapterList || chapterList.length === 0) &&
    Array.isArray(subQuestions)
  ) {
    const set = new Set<string>()
    for (const sq of subQuestions) {
      if (Array.isArray((sq as any).chapters)) {
        for (const name of (sq as any).chapters as string[])
          if (name) set.add(name)
      }
    }
    chapterList = Array.from(set)
  }
  const chapterRelated =
    chapterList && chapterList.length
      ? chapterList.join(', ')
      : item?.chapter || ''
  return {
    questionId: item.question_id,
    moodleQuestionId: item.moodle_question_id,
    question: item?.text || item?.question_text || '',
    subQuestion,
    marks,
    time,
    chapterRelated,
    avg_ai_marks: item?.avg_ai_marks,
    avg_tutor_marks: item?.avg_tutor_marks,
  }
}

function getTotals(items: QuestionDetail[]): {
  totalQuestions: number
  totalMarks: number
  totalTime: number
} {
  const totalQuestions = items.length
  let totalMarks = 0
  let totalTime = 0
  for (const q of items) {
    const subs = q?.sub_questions || []
    for (const sq of subs) {
      totalMarks += sq?.marks || 0
      totalTime += sq?.time_limit || 0
    }
  }
  return { totalQuestions, totalMarks, totalTime }
}

function getCaseStudyTotals(
  groups: Array<{
    caseStudyId: number
    items: CaseStudyQuestion[]
    totalTime: number
    totalMarks: number
    selectedCount: number
  }>
): { totalQuestions: number; totalMarks: number; totalTime: number } {
  if (!Array.isArray(groups) || groups.length === 0)
    return { totalQuestions: 0, totalMarks: 0, totalTime: 0 }
  let totalQuestions = 0
  let totalMarks = 0
  let totalTime = 0
  for (const g of groups) {
    totalQuestions += g?.selectedCount || 0
    totalMarks += g?.totalMarks || 0
    totalTime += g?.totalTime || 0
  }
  return { totalQuestions, totalMarks, totalTime }
}

// function recomputeGroupMetrics(items: CaseStudyQuestion[]) {
//   let totalTime = 0
//   let totalMarks = 0
//   for (const q of items) {
//     totalTime += q?.time || 0
//     totalMarks += q?.marks || 0
//   }
//   return { totalTime, totalMarks, selectedCount: items.length }
// }

function buildAllIds(
  nonCase: QuestionDetail[],
  csGroups: Array<{ caseStudyId: number; items: CaseStudyQuestion[] }>
): string[] {
  const ids: string[] = []
  for (const q of nonCase) ids.push(String(q.question_id))
  for (const g of csGroups)
    for (const q of g.items) ids.push(String(q.question_id))
  return ids
}

type SectionTableProps = {
  title: string
  moduleId: string
  onSaveSelected?: (payload: {
    section: string
    questionIds: string[]
    items: QuestionDetail[]
    caseStudyGroups: Array<{
      caseStudyId: number
      items: CaseStudyQuestion[]
      totalTime: number
      totalMarks: number
      selectedCount: number
    }>
    totals: { totalQuestions: number; totalMarks: number; totalTime: number }
    isUserInteraction?: boolean
  }) => void
  initialSelectedQuestions?: any[]
  initialSelectedCaseStudies?: any[]
}
export default function SectionTable({
  title,
  moduleId,
  onSaveSelected,
  initialSelectedQuestions,
  initialSelectedCaseStudies,
}: SectionTableProps) {
  const [selectedItems, setSelectedItems] = useState<QuestionDetail[]>([])
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<
    Array<{
      caseStudyId: number
      items: CaseStudyQuestion[]
      totalTime: number
      totalMarks: number
      selectedCount: number
    }>
  >([])

  // Prevent repeated parent notifications caused by prop/state feedback loops
  const initializedRef = useRef(false)
  const lastNotifyRef = useRef<string>('')

  const [quesntionId, setQuesionId] = useState<number>(0)
  const [typeValue, setTypeValue] = useState<string>('')

  // Helper: compute and notify parent using the provided next state
  function notifyParent(
    nextItems: QuestionDetail[],
    nextCs: Array<{
      caseStudyId: number
      items: CaseStudyQuestion[]
      totalTime: number
      totalMarks: number
      selectedCount: number
    }>,
    isUserInteraction: boolean = false
  ) {
    const nonCase = getTotals(nextItems)
    const cs = getCaseStudyTotals(nextCs as any)
    const totals = {
      totalQuestions: nonCase.totalQuestions + cs.totalQuestions,
      totalMarks: nonCase.totalMarks + cs.totalMarks,
      totalTime: nonCase.totalTime + cs.totalTime,
    }
    const questionIds = buildAllIds(nextItems, nextCs as any)
    // Create a unique key for this notification to prevent duplicate calls
    const notifyKey = JSON.stringify({
      questionIds: questionIds.sort(),
      totals,
      section: title,
    })

    // Only notify if the content actually changed
    if (lastNotifyRef.current === notifyKey) {
      return
    }

    lastNotifyRef.current = notifyKey
    onSaveSelected?.({
      section: title,
      questionIds,
      items: nextItems,
      caseStudyGroups: nextCs,
      totals,
      isUserInteraction,
    })
  }

  // Track previous props to avoid unnecessary updates
  const prevInitialQuestionsRef = useRef<string>('')
  const prevInitialCaseStudiesRef = useRef<string>('')

  // Track if user has interacted with the table/drawer
  const hasInteractedRef = useRef<boolean>(false)

  useEffect(() => {
    // If user has interacted, do not overwrite with props
    if (hasInteractedRef.current) return

    // Only hydrate from initial props once (or when they actually change)
    const nextItems = (initialSelectedQuestions as QuestionDetail[]) || []
    const nextCs = (initialSelectedCaseStudies || []).map((cs: any) => {
      const rawItems = Array.isArray(cs.items)
        ? cs.items
        : Array.isArray(cs.questions)
          ? cs.questions
          : []
      const items = filterCaseStudyQuestionsByQuestionIds(
        rawItems as CaseStudyQuestion[],
        cs.question_ids
      )
      let totalTime = 0
      let totalMarks = 0
      for (const q of items) {
        totalTime += (q as { time?: number }).time || 0
        totalMarks += (q as { marks?: number }).marks || 0
      }
      const caseStudyId = cs.case_study_id ?? cs.caseStudyId
      return {
        caseStudyId,
        items,
        totalTime: items.length
          ? totalTime
          : cs.total_time ?? cs.total_duration ?? 0,
        totalMarks: items.length
          ? totalMarks
          : cs.total_marks ?? cs.total_mark ?? 0,
        selectedCount:
          items.length ||
          (Array.isArray(cs.question_ids) ? cs.question_ids.length : 0) ||
          cs.total_questions ||
          0,
      }
    })

    const nextItemsStr = JSON.stringify(nextItems)
    const nextCsStr = JSON.stringify(nextCs)

    // Check if props actually changed from last time
    const itemsChanged = nextItemsStr !== prevInitialQuestionsRef.current
    const csChanged = nextCsStr !== prevInitialCaseStudiesRef.current

    if (!itemsChanged && !csChanged) return

    // Update refs
    prevInitialQuestionsRef.current = nextItemsStr
    prevInitialCaseStudiesRef.current = nextCsStr

    // If the new props match our current local state, it means we are "in sync"
    // So we can reset the interaction flag to allow future prop updates to take effect
    // (e.g. if the user saves, and then the parent re-renders with the same data,
    // we want to be ready for *new* data later)
    const currentItemsStr = JSON.stringify(selectedItems)
    const currentCsStr = JSON.stringify(selectedCaseStudy)

    if (nextItemsStr === currentItemsStr && nextCsStr === currentCsStr) {
      hasInteractedRef.current = false
    }

    // Update local state if we haven't interacted, OR if we just determined we are in sync
    if (!hasInteractedRef.current) {
      if (itemsChanged) setSelectedItems(nextItems)
      if (csChanged) setSelectedCaseStudy(nextCs)
      notifyParent(nextItems, nextCs as any, false)
    }

    // Notify parent only once on initial hydration to avoid update loops
    if (!initializedRef.current) {
      notifyParent(nextItems, nextCs as any, false)
      initializedRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedQuestions, initialSelectedCaseStudies])

  const rows: QuestionRow[] = useMemo(
    () => selectedItems.map(getRowFromQuestion),
    [selectedItems]
  )
  const {
    totalQuestions: nonCaseQuestions,
    totalMarks: nonCaseMarks,
    totalTime: nonCaseTime,
  } = useMemo(() => getTotals(selectedItems), [selectedItems])
  const {
    totalQuestions: caseStudyQuestions,
    totalMarks: caseStudyMarks,
    totalTime: caseStudyTime,
  } = useMemo(() => getCaseStudyTotals(selectedCaseStudy), [selectedCaseStudy])
  const totalQuestions = nonCaseQuestions + caseStudyQuestions
  const totalMarks = nonCaseMarks + caseStudyMarks
  const totalTime = nonCaseTime + caseStudyTime

  function handleRemoveNonCase(moodleQuestionId: string) {
    hasInteractedRef.current = true
    setSelectedItems((prev) => {
      const next = prev.filter((q) => q.moodle_question_id !== moodleQuestionId)
      notifyParent(next, selectedCaseStudy, true)
      return next
    })
  }

  function handleRemoveCaseStudyGroup(caseStudyId: number) {
    hasInteractedRef.current = true
    setSelectedCaseStudy((prev) => {
      const next = prev.filter((g) => g.caseStudyId !== caseStudyId)
      notifyParent(selectedItems, next, true)
      return next
    })
  }

  function handleRemoveCaseStudyItem(caseStudyId: number, questionId: number) {
    hasInteractedRef.current = true
    setSelectedCaseStudy((prev) => {
      const next: typeof prev = []
      for (const g of prev) {
        if (g.caseStudyId !== caseStudyId) {
          next.push(g)
          continue
        }
        const items = g.items.filter((q) => q.question_id !== questionId)
        if (items.length === 0) continue
        let totalTime = 0
        let totalMarks = 0
        for (const q of items) {
          totalTime += q.time || 0
          totalMarks += q.marks || 0
        }
        next.push({
          ...g,
          items,
          totalTime,
          totalMarks,
          selectedCount: items.length,
        })
      }
      notifyParent(selectedItems, next, true)
      return next
    })
  }

  // Column width mapping to keep Non-casestudy and Case Study tables aligned
  const COL = {
    id: 'w-[6%] min-w-[6%] max-w-[6%]',
    chapter: 'w-[20%] min-w-[20%] max-w-[20%]',
    question: 'w-[15%] min-w-[15%] max-w-[15%]',
    sub: 'w-[10%] text-center min-w-[10%] max-w-[10%]',
    time: 'w-[102px] text-center min-w-[102px]',
    marks: 'w-[102px] text-center min-w-[102px]',
    ai: 'w-[10%] text-center min-w-[10%] max-w-[10%]',
    tutor: 'w-[10%] text-center min-w-[10%] max-w-[10%]',
    action: 'w-[60px] min-w-[60px] max-w-[60px]',
  }

  return (
    <>
      <Stack gap={'12px'}>
        <SectionHeader
          title={title}
          section={title as QuestionSection}
          totalQuestion={totalQuestions}
          totalMarks={totalMarks}
          duration={totalTime}
          moduleId={moduleId}
          defaultSelectedIds={useMemo(
            () => selectedItems.map((q) => String(q.question_id)),
            [selectedItems]
          )}
          defaultCaseStudySelected={useMemo(
            () =>
              Object.fromEntries(
                selectedCaseStudy.map((g) => [
                  g.caseStudyId,
                  g.items.map((i) => String(i.question_id)),
                ])
              ),
            [selectedCaseStudy]
          )}
          onSaveSelected={({
            section,
            questionIds,
            items,
            caseStudyGroups,
          }) => {
            // Create unique key to prevent duplicate calls (include CS groups — same non-case ids can repeat after remove/re-add)
            const notifyKey = JSON.stringify({
              questionIds: questionIds.sort(),
              items: items.map((i) => i.question_id).sort(),
              caseStudies: caseStudyGroups
                .map((g) => [
                  g.caseStudyId,
                  ...g.items.map((q) => q.question_id).sort((a, b) => a - b),
                ])
                .sort((a, b) => a[0] - b[0]),
              section,
            })

            if (lastNotifyRef.current === notifyKey) {
              return
            }

            hasInteractedRef.current = true

            setSelectedItems(items)
            setSelectedCaseStudy(caseStudyGroups)
            const nextTotals = (() => {
              const nonCase = getTotals(items)
              const cs = getCaseStudyTotals(caseStudyGroups)
              return {
                totalQuestions: nonCase.totalQuestions + cs.totalQuestions,
                totalMarks: nonCase.totalMarks + cs.totalMarks,
                totalTime: nonCase.totalTime + cs.totalTime,
              }
            })()
            lastNotifyRef.current = notifyKey
            onSaveSelected?.({
              section,
              questionIds,
              items,
              caseStudyGroups,
              totals: nextTotals,
              isUserInteraction: true,
            })
          }}
        />
        {rows.length === 0 ? (
          <></>
        ) : (
          <Stack gap={'16px'} pb={'20px'} className='rounded-[16px]'>
            <Text className='text-[16px] font-normal text-[#1D2939]'>
              Non-Case Study Questions
            </Text>
            <DataTable
              mx={'0'}
              bgHeader='#ffffff'
              showFirstDivider
              showLastDivider
              className='rounded-md border bg-white'
            >
              {
                <>
                  <Table.ColumnHeaderCell className={`truncate ${COL.id}`}>
                    Question ID
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className={COL.chapter}>
                    Chapter
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className={COL.question}>
                    Question
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className={`${COL.sub} text-center`}>
                    Sub-question
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className={`${COL.time} text-center`}>
                    Time
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`${COL.marks} text-center`}
                  >
                    Marks Available
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className={`${COL.ai} text-center`}>
                    Average AI Mark
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`${COL.tutor} text-center`}
                  >
                    Average Tutor Mark
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={COL.action}
                  ></Table.ColumnHeaderCell>
                </>
              }
              {
                <>
                  {rows.map((item, index) => (
                    <Table.Row key={index}>
                      <Table.Cell
                        className={`font-medium text-[#002652] ${COL.id}`}
                      >
                        {item.moodleQuestionId}
                      </Table.Cell>
                      <Table.Cell
                        className={`truncate font-normal text-[#002652] ${COL.chapter}`}
                      >
                        {item.chapterRelated}
                      </Table.Cell>
                      <Table.Cell
                        className={`font-normal text-[#002652] ${COL.question}`}
                      >
                        {truncate(item.question, 30)}
                      </Table.Cell>
                      <Table.Cell
                        className={`font-normal text-[#002652] ${COL.sub}`}
                      >
                        {item.subQuestion}
                      </Table.Cell>
                      <Table.Cell
                        className={`font-normal text-[#002652] ${COL.time}`}
                      >
                        {item.time}
                      </Table.Cell>
                      <Table.Cell
                        className={`font-normal text-[#002652] ${COL.marks}`}
                      >
                        {item.marks}
                      </Table.Cell>
                      <Table.Cell
                        className={cn(
                          `font-normal ${COL.ai}`,
                          getMarksStyle(item?.avg_ai_marks)
                        )}
                      >
                        {getMarksPercentage(item?.avg_ai_marks)}
                      </Table.Cell>
                      <Table.Cell
                        className={cn(
                          `font-normal ${COL.tutor}`,
                          getMarksStyle(item?.avg_tutor_marks)
                        )}
                      >
                        {getMarksPercentage(item?.avg_tutor_marks)}
                      </Table.Cell>
                      <Table.Cell
                        className={`hover:cursor-pointer ${COL.action}`}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='ghost' className='h-8 w-8 p-0'>
                              <span className='sr-only'>Open menu</span>
                              <IconDotsVertical />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuItem
                              className='cursor-pointer'
                              onClick={() => {
                                // Clear the case-study type a previous View may have
                                // left, or the drawer asks the case-study endpoint for
                                // an ordinary question.
                                setTypeValue('')
                                setQuesionId(item.questionId)
                              }}
                            >
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className='cursor-pointer'
                              onClick={() =>
                                handleRemoveNonCase(item.moodleQuestionId)
                              }
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </>
              }
            </DataTable>
            <TablePaginate
              className='justify-end pr-[24px]'
              page={1}
              totalPages={1}
              onPageChange={() => {}}
            />
          </Stack>
        )}

        {selectedCaseStudy.length === 0 ? (
          <></>
        ) : (
          <Stack gap={'16px'} className='rounded-[16px]'>
            <Hstack justify={'between'}>
              <Text className='text-[16px] font-normal text-[#1D2939]'>
                Case Study
              </Text>
              {/* <Hstack gap={'24px'}>
                <Text className='text-[14px] text-[#1D2939]'>
                  Duration:{' '}
                  <span className='font-medium'>{caseStudyTime} minutes</span>
                </Text>
                <Text className='text-[14px] text-[#1D2939]'>
                  Total marks:{' '}
                  <span className='font-medium'>{caseStudyMarks}</span>
                </Text>
              </Hstack> */}
            </Hstack>
            <Accordion type='multiple' className='rounded-md border bg-white'>
              {selectedCaseStudy.map((group) => (
                <AccordionItem
                  key={`sel-cs-${group.caseStudyId}`}
                  value={`sel-cs-${group.caseStudyId}`}
                >
                  <AccordionTrigger className='px-4'>
                    <Hstack justify={'between'} className='w-full'>
                      <Hstack gap={'8px'}>
                        <Text className='text-[14px] font-medium text-[#002652]'>
                          Case Study {group.caseStudyId}
                        </Text>
                        <Text className='text-[14px] text-[#475467]'>
                          ({group.selectedCount} Question)
                        </Text>
                      </Hstack>
                      <Hstack gap={'24px'}>
                        <Text className='text-[14px] text-[#1D2939]'>
                          Duration:{' '}
                          <span className='font-medium'>
                            {group.totalTime} minutes
                          </span>
                        </Text>
                        <Text className='text-[14px] text-[#1D2939]'>
                          Total Marks Available:{' '}
                          <span className='font-medium'>
                            {group.totalMarks}
                          </span>
                        </Text>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild className='mr-4'>
                            <Button
                              variant='ghost'
                              className='h-6 w-6 p-0 text-[#1D2939] hover:bg-transparent'
                              onClick={(e) => e.stopPropagation()}
                              aria-label='Open actions'
                            >
                              <IconDotsVertical />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align='end'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenuItem
                              onClick={() =>
                                handleRemoveCaseStudyGroup(group.caseStudyId)
                              }
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </Hstack>
                    </Hstack>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className='overflow-hidden px-4'>
                      <UiTable className='w-full table-fixed border-b'>
                        <UiTableHeader>
                          <UiTableRow>
                            <UiTableHead className={`text-[#002652]`}>
                              Question ID
                            </UiTableHead>
                            <UiTableHead
                              className={`text-[#002652] ${COL.chapter}`}
                            >
                              Chapter
                            </UiTableHead>
                            <UiTableHead
                              className={`text-[#002652] ${COL.question}`}
                            >
                              Question
                            </UiTableHead>
                            <UiTableHead
                              className={`text-center text-[#002652] ${COL.sub}`}
                            >
                              Sub-question
                            </UiTableHead>
                            <UiTableHead
                              className={`text-center text-[#002652] ${COL.time}`}
                            >
                              Time
                            </UiTableHead>
                            <UiTableHead
                              className={`text-center text-[#002652] ${COL.marks}`}
                            >
                              Marks Available
                            </UiTableHead>
                            <UiTableHead className={`text-[#002652] ${COL.ai}`}>
                              Average AI Mark
                            </UiTableHead>
                            <UiTableHead
                              className={`text-center text-[#002652] ${COL.ai}`}
                            >
                              Average Tutor Mark
                            </UiTableHead>
                            <UiTableHead
                              className={`text-[#002652] ${COL.action}`}
                            ></UiTableHead>
                          </UiTableRow>
                        </UiTableHeader>
                        <UiTableBody>
                          {group.items.length ? (
                            group.items.map((q, idx) => (
                              <UiTableRow key={`g-${group.caseStudyId}-${idx}`}>
                                <UiTableCell
                                  className={`font-medium text-[#002652] ${COL.id}`}
                                >
                                  {q.moodle_question_id}
                                </UiTableCell>
                                <UiTableCell
                                  className={`text-[#002652] ${COL.chapter}`}
                                >
                                  {truncate(
                                    Array.isArray(q.chapter_name)
                                      ? q.chapter_name.join(', ')
                                      : String(q.chapter_name || ''),
                                    30
                                  )}
                                </UiTableCell>
                                <UiTableCell
                                  className={`text-[#002652] ${COL.question}`}
                                >
                                  {truncate(q.text, 30)}
                                </UiTableCell>
                                <UiTableCell
                                  className={`text-[#002652] ${COL.sub}`}
                                >
                                  {q.sub_questions}
                                </UiTableCell>
                                <UiTableCell
                                  className={`text-[#002652] ${COL.time}`}
                                >
                                  {q.time}
                                </UiTableCell>
                                <UiTableCell
                                  className={`text-[#002652] ${COL.marks}`}
                                >
                                  {q.marks}
                                </UiTableCell>
                                <Table.Cell
                                  className={cn(
                                    `font-normal ${COL.ai} !px-6`,
                                    getMarksStyle(q?.avg_ai_marks)
                                  )}
                                >
                                  {getMarksPercentage(q?.avg_ai_marks)}
                                </Table.Cell>
                                <Table.Cell
                                  className={cn(
                                    `font-normal ${COL.tutor} !px-6`,
                                    getMarksStyle(q?.avg_tutor_marks)
                                  )}
                                >
                                  {getMarksPercentage(q?.avg_tutor_marks)}
                                </Table.Cell>
                                <UiTableCell
                                  className={`hover:cursor-pointer ${COL.action}`}
                                >
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        className='h-8 w-8 p-0'
                                      >
                                        <span className='sr-only'>
                                          Open menu
                                        </span>
                                        <IconDotsVertical />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align='end'>
                                      <DropdownMenuItem
                                        className='cursor-pointer'
                                        onClick={() => {
                                          setQuesionId(q.question_id)
                                          setTypeValue(TypeValue.case_study)
                                        }}
                                      >
                                        View
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className='cursor-pointer'
                                        onClick={() =>
                                          handleRemoveCaseStudyItem(
                                            group.caseStudyId,
                                            q.question_id
                                          )
                                        }
                                      >
                                        Remove
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </UiTableCell>
                              </UiTableRow>
                            ))
                          ) : (
                            <UiTableRow>
                              <UiTableCell
                                colSpan={9}
                                className='h-24 text-center'
                              >
                                No results.
                              </UiTableCell>
                            </UiTableRow>
                          )}
                        </UiTableBody>
                      </UiTable>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Stack>
        )}
      </Stack>
      {!!quesntionId && (
        <QuestionDrawer
          fromChapterTable={true}
          questionId={quesntionId}
          setIsOpen={setQuesionId}
          typeValue={typeValue}
        />
      )}
    </>
  )
}
